import { useEffect, useRef } from "react";
import { db } from "../firebase";
import { ref, onValue } from "firebase/database";
import { useAuth } from "../context/AuthContext";
import { initNotifications } from "../utils/notifications";

export function useNotifications() {
  const { currentUser } = useAuth();
  const seenIds = useRef(new Set());
  const startedAt = useRef(Date.now());
  const fcmInitDone = useRef(false);

  useEffect(() => {
    if (!currentUser) return;
    if (!("Notification" in window)) return;

    if (Notification.permission === "granted" && !fcmInitDone.current) {
      fcmInitDone.current = true;
      initNotifications(currentUser.uid);
      return;
    }

    // Request permission — only prompt once, after 5 second delay
    if (Notification.permission === "default") {
      const t = setTimeout(() => {
        Notification.requestPermission().then((perm) => {
          if (perm === "granted" && !fcmInitDone.current) {
            fcmInitDone.current = true;
            initNotifications(currentUser.uid);
          }
        });
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;

    const notifRef = ref(db, `notifications/${currentUser.uid}`);

    const unsub = onValue(notifRef, (snapshot) => {
      if (!snapshot.exists()) return;

      const all = snapshot.val();
      Object.entries(all).forEach(([id, notif]) => {
        // Skip already seen, already read, or old notifications
        if (seenIds.current.has(id)) return;
        if (notif.read) { seenIds.current.add(id); return; }
        if ((notif.createdAt || 0) < startedAt.current - 3000) {
          seenIds.current.add(id);
          return;
        }

        seenIds.current.add(id);

        // Only show browser notification if app is not in focus
        if (document.visibilityState !== "visible") {
          const n = new Notification("CampusSwap", {
            body: notif.message || "You have a new notification",
            icon: "/logo192.png",
            badge: "/logo192.png",
            tag: id, // prevents duplicate notifications
          });
          // Clicking notification focuses the app
          n.onclick = () => { window.focus(); n.close(); };
        }
      });
    });

    return () => unsub();
  }, [currentUser]);
}
